// Preferência de VISÃO da agenda (VF-3, correção) — pura.
//
// A agenda voltava sempre para "dia". Quem trabalha na semana saía da tela e
// voltava para o dia, toda vez.
//
// ★ POR QUE `localStorage` AQUI, se o `lib/estadoDeSessao.js` o proíbe: o que
// ele proíbe é DADO DE PACIENTE — a regra de LGPD do projeto. Qual visão a
// pessoa prefere não é dado de paciente; é preferência de tela, igual às seções
// da inbox (`conversas/secoesInbox.js`) e ao som de notificação
// (`conversas/notificacaoSom.js`), e este módulo segue o mesmo padrão deles.
//
// ★ A CHAVE LEVA O UID. O computador da recepção é COMPARTILHADO: sem o uid, a
// preferência de quem entrou de manhã valeria para quem entrar à tarde.
//
// ⚠ Todo acesso em `try/catch`: só TOCAR em `globalThis.localStorage` já lança
// em navegador com cookies de terceiros bloqueados, e uma preferência de tela
// nunca pode derrubar a página.

const VISOES_VALIDAS = ['dia', 'semana', 'mes'];
/**
 * ★★ A-3(d), 11/out/2026 — O PADRÃO VIROU `semana`. Era `dia`.
 *
 * Decisão do dono: a agenda abre na SEMANA, em hoje. O dia continua existindo
 * como visão e continua sendo lembrado se a pessoa o escolher — o que mudou é
 * só onde ela cai quando NÃO escolheu nada.
 */
export const VISAO_PADRAO = 'semana';

export const chaveDaVisao = (uid) => `odontosdr:agenda-visao:${uid ?? 'anonimo'}`;

/**
 * ★★★ A-3(d) — O REINÍCIO DE UMA VEZ SÓ, e por que ele precisa existir.
 *
 * ⚠⚠ Trocar `VISAO_PADRAO` sozinho **não muda nada para quem já usa a tela**: a
 * preferência da Jéssica, da Damiana e da Talita já está gravada, e
 * `carregarVisao` a devolve. O padrão novo só valeria para quem nunca escolheu —
 * exatamente as pessoas que não pediram a mudança.
 *
 * ★ Então uma vez, e **uma só**, a escolha guardada é descartada: todas caem na
 * semana na próxima abertura, e da segunda em diante a tela volta a lembrar o
 * que a pessoa escolher.
 *
 * ★★★ COMO ELE NÃO APAGA OUTRAS PREFERÊNCIAS — é a parte que importa:
 *
 *   1. ⚠⚠⚠ **Nada de `localStorage.clear()`.** Ele levaria as seções da inbox, o
 *      som de notificação e a seleção de conversa junto. O `removeItem` daqui
 *      aponta para **uma** chave: `chaveDaVisao(uid)`;
 *   2. a marca de "já reiniciei" é chave **própria e versionada**
 *      (`chaveDoReinicio`), então ela não pode colidir com a da visão;
 *   3. ★ a marca leva o **uid**, como a visão: o computador da recepção é
 *      compartilhado, e reiniciar a visão da Damiana não pode consumir o
 *      reinício da Talita;
 *   4. a função devolve `true` só quando **agiu**, e o teste usa isso para
 *      provar que a segunda chamada não mexe em nada.
 *
 * ★ Para reiniciar de novo um dia, troca-se o `:v1` por `:v2` — a marca antiga
 * fica órfã (algumas dezenas de bytes) e ninguém tem de caçar chave para apagar.
 */
export const chaveDoReinicio = (uid) => `odontosdr:agenda-visao-reinicio:v1:${uid ?? 'anonimo'}`;

function deposito() {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * Descarta a visão guardada — UMA VEZ por pessoa. Devolve `true` se agiu.
 *
 * ⚠ Chamar antes de `carregarVisao`, nunca depois: depois, a visão velha já
 * estaria no estado da tela.
 */
export function reiniciarVisaoUmaVez(uid, armazem = deposito()) {
  if (!armazem) return false;
  try {
    if (armazem.getItem(chaveDoReinicio(uid))) return false;
    // ★ A ORDEM IMPORTA: a marca vai PRIMEIRO. Se o `removeItem` falhar (cota,
    // modo privado), a pessoa fica com a visão antiga e a tela segue — mas a
    // marca gravada impede o laço de tentar o reinício em toda abertura.
    armazem.setItem(chaveDoReinicio(uid), '1');
    // ⚠⚠ UMA chave, a da visão desta pessoa. Nada de `clear()`.
    armazem.removeItem(chaveDaVisao(uid));
    return true;
  } catch {
    return false;
  }
}

/** Visão guardada, ou o padrão. Valor desconhecido NÃO passa. */
export function carregarVisao(uid, armazem = deposito()) {
  try {
    const cru = armazem?.getItem(chaveDaVisao(uid));
    return VISOES_VALIDAS.includes(cru) ? cru : VISAO_PADRAO;
  } catch {
    return VISAO_PADRAO;
  }
}

/** Guarda a visão. Valor inválido é ignorado — nunca grava lixo. */
export function salvarVisao(uid, visao, armazem = deposito()) {
  if (!VISOES_VALIDAS.includes(visao)) return false;
  // ⚠ Sem depósito NÃO é sucesso. `armazem?.setItem()` seguido de `return true`
  // devolvia "gravei" sem ter gravado nada — um valor de retorno que mente é
  // pior que nenhum. (A guarda é explícita de propósito: sem ela o
  // `null.setItem` cairia no `catch` abaixo e o resultado seria o mesmo, mas por
  // acidente. Intenção escrita vale mais que efeito colateral correto.)
  if (!armazem) return false;
  try {
    armazem.setItem(chaveDaVisao(uid), visao);
    return true;
  } catch {
    return false;
  }
}
